// DS: COF1.1
import React, { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import ConditionalWrapper from '@wix/da-shared-react/pkg/ConditionalWrapper';
import useModuleData from '../../../../utils/hooks/useModuleData';
import EmptyState from '../../EmptyState';
import WidgetPaywall from '../_partials/paywalls/WidgetPaywall';
import ChunkLoadingPopover from '../_partials/ChunkLoadingPopover';
import WidgetBox, { Props as WidgetBoxProps } from '../_partials/WidgetBox';
import reduxConnectContainer from './LlamaFarm.container';
import { default as LlamaFarm, Props as WidgetProps } from './LlamaFarm';

import s from './LlamaFarm.scss';

const EditPopup = lazy(
  () => import(/* webpackChunkName: "owner" */ './EditPopup')
);

const LlamaFarmBox: React.FC<WidgetProps & WidgetBoxProps> = ({
  widgetMoveUp,
  widgetMoveDown,
  widgetRemove,
  section,
  zone,
  ...props
}) => {
  const { t } = useTranslation();
  const { moduleData = {} } = useModuleData(props.moduleId);
  const renderConfigPopup = close => {
    const { moduleId } = props;
    return (
      <Suspense fallback={<ChunkLoadingPopover />}>
        <EditPopup moduleId={moduleId} onRequestClose={close} />
      </Suspense>
    );
  };

  const emptyStateView = 'large';
  const emptyStateClassName = s['empty-state'];
  const { llamas = [] } = moduleData;
  const isEmpty = llamas.length === 0;

  return (
    <WidgetBox
      section={section}
      zone={zone}
      moduleId={props.moduleId}
      header={props.title || t('widgets.llamafarm.header')}
      autoShowWidgetConfig={true}
      widgetMenuRenderer={renderConfigPopup}
      widgetMoveUp={widgetMoveUp}
      widgetMoveDown={widgetMoveDown}
      widgetRemove={widgetRemove}
      isEmpty={isEmpty}
      emptyView={
        props.isOwner && (
          <EmptyState
            view={emptyStateView}
            className={emptyStateClassName}
            title={t('widgets.llamafarm.empty.title.owner')}
            subtitle={t('widgets.llamafarm.empty.subtitle.owner')}
          />
        )
      }
    >
      <ConditionalWrapper
        condition={props.isOwner && !props.isCurrentUserCoreMember}
        wrap={child => (
          <WidgetPaywall
            previewReferral={ReferralType.LLAMAFARM_WIDGET_PAYWALL_PREVIEW}
            lockedReferral={ReferralType.LLAMAFARM_WIDGET_PAYWALL_OVERLAY}
          >
            {child}
          </WidgetPaywall>
        )}
      >
        <LlamaFarm {...props} zone={zone} />
      </ConditionalWrapper>
    </WidgetBox>
  );
};

export default reduxConnectContainer(LlamaFarmBox);
